import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Home, History, Ruler, Palette, MessageCircle, Package, ChevronDown, ChevronRight, Plus, Trash2, Download, Phone, MapPin, Globe, Crown, AlertTriangle, Check, X, Paintbrush } from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import DesignCreator from './DesignCreator';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const EMPTY_LINE = { nom: '', numero: '', taille: '' };
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

const SOCIAL_LINKS = {
  vi: {
    facebook: { url: "https://www.facebook.com/PantherInVietnam", label: "Facebook" },
    instagram: { url: "https://www.instagram.com/pantherinvietnam/", label: "Instagram" },
    tiktok: { url: "https://www.tiktok.com/@pantherdesignforyou", label: "TikTok" },
    youtube: { url: "https://www.youtube.com/@pantherdesign", label: "YouTube" },
  },
  fr: {
    facebook: { url: "https://www.facebook.com/PantherDesignForYou", label: "Facebook" },
    instagram: { url: "https://www.instagram.com/pantherdesignforyou/", label: "Instagram" },
    tiktok: { url: "https://www.tiktok.com/@pantherfr", label: "TikTok" },
    youtube: { url: "https://www.youtube.com/@pantherdesign", label: "YouTube" },
  },
  en: {
    facebook: { url: "https://www.facebook.com/PantherDesignForYou", label: "Facebook" },
    instagram: { url: "https://www.instagram.com/pantherdesignforyou/", label: "Instagram" },
    tiktok: { url: "https://www.tiktok.com/@pantherfr", label: "TikTok" },
    youtube: { url: "https://www.youtube.com/@pantherdesign", label: "YouTube" },
  },
};

export default function PremiumDashboard() {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [clientInfo, setClientInfo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});
  const [persoLines, setPersoLines] = useState([{ ...EMPTY_LINE }]);
  const [persoExporting, setPersoExporting] = useState(false);
  const [premiumInfo, setPremiumInfo] = useState(null);
  const [levelDialogOpen, setLevelDialogOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [meRes, dashRes, histRes, premRes] = await Promise.all([
        axios.get(`${API}/api/client/me`, { withCredentials: true }),
        axios.get(`${API}/api/client/dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/client/orders/history`, { withCredentials: true }),
        axios.get(`${API}/api/client/premium-info`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setClientInfo(meRes.data);
      setDashboard(dashRes.data);
      setHistory(histRes.data);
      setPremiumInfo(premRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/espace-client');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await axios.post(`${API}/api/client/logout`, {}, { withCredentials: true });
    navigate('/espace-client');
  };

  const handlePersoExport = async () => {
    const validLines = persoLines.filter(l => l.nom.trim());
    if (!validLines.length) { toast.error('Ajoutez au moins un nom'); return; }
    setPersoExporting(true);
    try {
      const res = await axios.post(`${API}/api/personalization/export`, {
        client_name: `${clientInfo?.firstname || ''} ${clientInfo?.name || ''}`.trim(),
        lines: validLines,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'personnalisation.xlsx'; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Erreur export'); }
    finally { setPersoExporting(false); }
  };

  const historyByYear = {};
  history.forEach(o => { const yr = o.year || 'Inconnu'; if (!historyByYear[yr]) historyByYear[yr] = []; historyByYear[yr].push(o); });
  const sortedYears = Object.keys(historyByYear).sort((a, b) => b - a);

  const socials = SOCIAL_LINKS[language] || SOCIAL_LINKS.fr;

  const getLevelBenefits = (lvl) => lvl?.[`benefits_${language}`] || lvl?.benefits_en || [];
  const getLevelExcluded = (lvl) => lvl?.[`excluded_${language}`] || lvl?.excluded_en || [];

  const fmtVND = (v) => v ? `${(v / 1_000_000).toFixed(0)}M VND` : '0 VND';

  const sidebarItems = [
    { id: 'home', label: t('premium_home'), icon: Home },
    { id: 'design', label: language === 'vi' ? 'Tạo thiết kế' : language === 'en' ? 'Design Creator' : 'Création de design', icon: Paintbrush },
    { id: 'history', label: t('premium_archives'), icon: History },
    { id: 'size-guide', label: t('premium_size_guide'), icon: Ruler },
    { id: 'personalization', label: t('premium_personalization'), icon: Palette },
    { id: 'contact', label: t('premium_contact'), icon: MessageCircle },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-9 object-contain" />
            <div>
              <p className="text-sm font-medium text-slate-900">{clientInfo?.firstname} {clientInfo?.name}</p>
              <p className="text-xs text-slate-500">{clientInfo?.company_name} — {t('premium_client_space')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-28 h-8 text-xs border-slate-200">
                <Globe size={14} className="mr-1" />
                <SelectValue>{LANG_LABELS[language]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="vi">Tiếng Việt</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="premium-logout-btn" onClick={handleLogout} variant="ghost" className="text-slate-600 text-sm">
              <LogOut size={16} className="mr-2" /> {t('premium_logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0" data-testid="premium-sidebar">
          {clientInfo?.logo_url && (
            <div className="p-4 border-b border-slate-100 flex justify-center">
              <img src={clientInfo.logo_url} alt={clientInfo.company_name || 'Logo'} className="max-h-20 max-w-[180px] object-contain" />
            </div>
          )}
          <nav className="p-3 space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button key={item.id} data-testid={`premium-tab-${item.id}`} onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <Icon size={18} /> <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 space-y-6 overflow-auto">

          {/* ===== ACCUEIL ===== */}
          {activeTab === 'home' && dashboard && (
            <div className="space-y-6">
              {/* Premium Title */}
              <div className="text-center">
                <h1 data-testid="premium-title" className="text-2xl font-bold tracking-wider" style={{ color: '#c3263b' }}>
                  {t('premium_title')}
                </h1>
              </div>

              {/* Loyalty Levels + Reminder + Pricing — single row */}
              {premiumInfo && premiumInfo.levels && (() => {
                const currentLvl = premiumInfo.current_level_idx >= 0 ? premiumInfo.levels[premiumInfo.current_level_idx] : null;
                const currentDiscount = currentLvl?.discount || 0;
                const LEVEL_ICONS = {
                  jewel: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
                  silver: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>,
                  prime: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/><path d="M3 20h18"/></svg>,
                  gold: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/><path d="M3 20h18"/><circle cx="12" cy="4" r="1" fill="currentColor"/><circle cx="6" cy="11" r="1" fill="currentColor"/><circle cx="18" cy="11" r="1" fill="currentColor"/></svg>,
                };
                return (
                  <div className="flex gap-3 items-stretch">
                    {/* 4 Level cards */}
                    {premiumInfo.levels.map((lvl, i) => {
                      const isCurrent = i === premiumInfo.current_level_idx;
                      return (
                        <button key={lvl.id} data-testid={`premium-level-${lvl.id}`}
                          onClick={() => { setSelectedLevel(lvl); setLevelDialogOpen(true); }}
                          className={`relative flex-1 p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${
                            isCurrent ? 'bg-white shadow-lg scale-[1.03]' : 'bg-slate-100 border-slate-200 opacity-50 grayscale hover:opacity-70'
                          }`}
                          style={isCurrent ? { borderColor: lvl.color, boxShadow: `0 4px 20px ${lvl.color}30` } : {}}>
                          {isCurrent && (
                            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-white text-[9px] font-bold whitespace-nowrap" style={{ backgroundColor: lvl.color }}>
                              {language === 'vi' ? 'HIỆN TẠI' : language === 'en' ? 'CURRENT' : 'ACTUEL'}
                            </div>
                          )}
                          <div className="flex justify-center mt-1" style={{ color: isCurrent ? lvl.color : '#94a3b8' }}>
                            {LEVEL_ICONS[lvl.id]}
                          </div>
                          <p className="font-bold text-xs mt-1.5" style={{ color: isCurrent ? lvl.color : '#94a3b8' }}>{lvl.name}</p>
                          <p className={`text-sm font-black ${isCurrent ? 'text-slate-900' : 'text-slate-400'}`}>{fmtVND(lvl.threshold)}</p>
                          <p className="text-[9px] text-slate-400">{language === 'vi' ? 'mỗi năm' : language === 'en' ? 'per year' : 'par an'}</p>
                          <p className="text-[10px] font-semibold mt-1" style={{ color: isCurrent ? lvl.color : '#94a3b8' }}>-{lvl.discount}%</p>
                        </button>
                      );
                    })}

                    {/* Reminder card */}
                    {premiumInfo.deadline && (
                      <div className="flex-1 min-w-[180px] bg-amber-50 border-2 border-amber-200 rounded-xl p-3 flex flex-col justify-center">
                        <div className="flex items-center gap-1.5 mb-1">
                          <AlertTriangle size={14} className="text-amber-600" />
                          <span className="text-[10px] font-bold text-amber-800 uppercase">
                            {premiumInfo.current_level_idx >= 0 && premiumInfo.remaining_to_maintain > 0
                              ? (language === 'vi' ? 'Duy trì cấp độ' : language === 'en' ? 'Keep level' : 'Garder le niveau')
                              : (language === 'vi' ? 'Cấp tiếp theo' : language === 'en' ? 'Next level' : 'Niveau suivant')}
                          </span>
                        </div>
                        {premiumInfo.current_level_idx >= 0 && premiumInfo.remaining_to_maintain > 0 ? (
                          <>
                            <p className="text-xs text-amber-900">{language === 'vi' ? 'Còn' : language === 'en' ? 'Remaining' : 'Il reste'}</p>
                            <p className="text-sm font-black text-amber-700">{Math.ceil(premiumInfo.remaining_to_maintain).toLocaleString()} VND</p>
                            <p className="text-[10px] text-amber-600 mt-0.5">
                              {language === 'vi' ? 'trước' : language === 'en' ? 'before' : 'avant le'} {new Date(premiumInfo.deadline).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'en' ? 'en-US' : 'fr-FR')}
                            </p>
                          </>
                        ) : premiumInfo.remaining_to_next > 0 ? (
                          <>
                            <p className="text-xs text-amber-900">{language === 'vi' ? 'Còn' : language === 'en' ? 'Remaining' : 'Il reste'}</p>
                            <p className="text-sm font-black text-amber-700">{Math.ceil(premiumInfo.remaining_to_next).toLocaleString()} VND</p>
                            <p className="text-[10px] text-amber-600 mt-0.5">
                              {language === 'vi' ? 'để đạt' : language === 'en' ? 'to reach' : 'pour'} <strong>{premiumInfo.levels[premiumInfo.current_level_idx + 1]?.name}</strong>
                            </p>
                          </>
                        ) : (
                          <p className="text-xs font-semibold text-emerald-700">
                            {language === 'vi' ? 'Cấp cao nhất!' : language === 'en' ? 'Max level!' : 'Niveau max !'}
                          </p>
                        )}
                        <p className="text-[9px] text-amber-500 mt-1">CA: {Math.floor(premiumInfo.annual_revenue_vnd || 0).toLocaleString()} VND</p>
                      </div>
                    )}

                    {/* Pricing card — only current level */}
                    {premiumInfo.product_prices && Object.keys(premiumInfo.product_prices).length > 0 && (
                      <div className="flex-1 min-w-[180px] bg-white border-2 rounded-xl p-3 flex flex-col justify-center" style={{ borderColor: currentLvl?.color || '#e2e8f0' }}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Crown size={14} style={{ color: currentLvl?.color || '#94a3b8' }} />
                          <span className="text-[10px] font-bold uppercase" style={{ color: currentLvl?.color || '#94a3b8' }}>
                            {language === 'vi' ? 'Giá của bạn' : language === 'en' ? 'Your prices' : 'Vos prix'} — {currentLvl?.name || 'N/A'}
                          </span>
                        </div>
                        {Object.entries(premiumInfo.product_prices).map(([product, price]) => {
                          const discounted = currentDiscount > 0 ? Math.round(Number(price) * (1 - currentDiscount / 100)) : Number(price);
                          return (
                            <div key={product} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                              <span className="text-xs text-slate-700">{product}</span>
                              <div className="text-right">
                                {currentDiscount > 0 && <span className="text-[9px] text-slate-400 line-through mr-1.5">{Number(price).toLocaleString()}</span>}
                                <span className="text-xs font-bold" style={{ color: currentLvl?.color || '#1e293b' }}>{discounted.toLocaleString()} VND</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Stats + Address */}
              <div className="flex gap-4">
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <Card className="border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <Package size={20} className="mx-auto text-blue-600 mb-1" />
                      <p className="text-2xl font-bold text-slate-900">{dashboard.active_orders?.length || 0}</p>
                      <p className="text-xs text-slate-500">{t('premium_orders_active')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <History size={20} className="mx-auto text-emerald-600 mb-1" />
                      <p className="text-2xl font-bold text-slate-900">{dashboard.total_orders || 0}</p>
                      <p className="text-xs text-slate-500">{t('premium_orders_total')}</p>
                    </CardContent>
                  </Card>
                </div>
                {(clientInfo?.address || clientInfo?.city || clientInfo?.country) && (
                  <Card className="border-slate-200 w-72 flex-shrink-0">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-2">
                        <MapPin size={16} className="text-slate-500 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-slate-700">
                          <p className="font-semibold text-slate-900 mb-1 text-sm">{t('premium_delivery_address')}</p>
                          <p className="font-medium">{clientInfo.firstname} {clientInfo.name}</p>
                          {clientInfo.address && <p>{clientInfo.address}</p>}
                          <p>{[clientInfo.postal_code, clientInfo.city].filter(Boolean).join(' ')}</p>
                          {clientInfo.country && <p>{clientInfo.country}</p>}
                          {clientInfo.phone && <p className="mt-1">Tel : {clientInfo.phone}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Active Orders */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">{t('premium_orders_active')} ({dashboard.active_orders?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {(!dashboard.active_orders || dashboard.active_orders.length === 0) ? (
                    <p className="text-sm text-slate-400 text-center py-4">{t('premium_no_orders')}</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium">Date</TableHead>
                          <TableHead className="font-medium">{t('premium_orders_active')}</TableHead>
                          <TableHead className="font-medium">{t('status')}</TableHead>
                          <TableHead className="font-medium">{t('suivi')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboard.active_orders.map(o => (
                          <TableRow key={o._id}>
                            <TableCell className="text-xs">{o.order_date?.slice(0, 10)}</TableCell>
                            <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                            <TableCell><Badge className="bg-blue-100 text-blue-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                            <TableCell>
                              {o.tracking_number ? (
                                <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                  <Package size={12} /> {t('suivi')}
                                </Button>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Conditions de paiement */}
              <Card className="border-slate-200">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-900 mb-3" style={{ color: '#c3263b' }}>{t('premium_payment_title')}</h3>
                  <div className="bg-emerald-50/50 border border-emerald-100 p-5 rounded-xl text-sm">
                    <p className="font-semibold">Modalités :</p>
                    <ul className="mt-2 ml-5 list-disc space-y-1">
                      <li><strong>50% d'acompte</strong> à la validation de la commande.</li>
                      <li><strong>Le solde</strong> lorsque la fabrication est terminée.</li>
                    </ul>
                    <p className="mt-4 text-xs text-slate-600">
                      Nos prix incluent les <strong>frais de livraison standard</strong> (pour les commandes supérieures à 1400€ HT) ainsi que les <strong>frais de dédouanement</strong>.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Réseaux sociaux */}
              <Card className="border-slate-200">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-900 mb-4">{t('premium_social_title')}</h3>
                  <div className="flex gap-4 flex-wrap">
                    <a href={socials.instagram.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-pink-50 hover:border-pink-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-600"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                      {socials.instagram.label}
                    </a>
                    <a href={socials.facebook.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-blue-600"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                      {socials.facebook.label}
                    </a>
                    <a href={socials.youtube.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-red-600"><path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.43z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="white"/></svg>
                      {socials.youtube.label}
                    </a>
                    <a href={socials.tiktok.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-100 hover:border-slate-400 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-slate-900"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.8a4.84 4.84 0 01-1-.11z"/></svg>
                      {socials.tiktok.label}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}


          {/* ===== DESIGN CREATOR ===== */}
          {activeTab === 'design' && <DesignCreator />}

          {/* ===== ARCHIVES ===== */}
          {activeTab === 'history' && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">{t('premium_order_archives')}</CardTitle></CardHeader>
              <CardContent>
                {sortedYears.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">{t('premium_no_archives')}</p>
                ) : (
                  <div className="space-y-3">
                    {sortedYears.map(year => (
                      <div key={year} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                          onClick={() => setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }))}>
                          <div className="flex items-center gap-2">
                            {expandedYears[year] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="font-semibold text-slate-900">{year}</span>
                          </div>
                          <Badge className="bg-slate-200 text-slate-700 border-0">{historyByYear[year].length}</Badge>
                        </button>
                        {expandedYears[year] && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-medium">{t('order_number')}</TableHead>
                                <TableHead className="font-medium">{t('status')}</TableHead>
                                <TableHead className="font-medium">{t('suivi')}</TableHead>
                                <TableHead className="font-medium">Date</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {historyByYear[year].map(o => (
                                <TableRow key={o._id}>
                                  <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                                  <TableCell><Badge className="bg-emerald-100 text-emerald-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                                  <TableCell>
                                    {o.tracking_number ? (
                                      <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                        <Package size={12} /> {t('suivi')}
                                      </Button>
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                  </TableCell>
                                  <TableCell className="text-xs">{o.delivery_date ? new Date(o.delivery_date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===== GUIDE DES TAILLES ===== */}
          {activeTab === 'size-guide' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="premium-size-guide">
              <h2 style={{ color: '#c3263b', borderLeft: '5px solid #c3263b' }} className="pl-4 mb-5 uppercase text-sm font-bold">{t('premium_size_guide')}</h2>
              <div className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ADULTES</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['XS','45-50 kg','160-166 cm'],['S','50-55 kg','166-170 cm'],['M','55-65 kg','170-175 cm'],['L','65-75 kg','175-180 cm'],['XL','75-85 kg','180-185 cm'],['2XL','85-95 kg','185-190 cm'],['3XL','95-110 kg','190-195 cm'],['4XL','110-115 kg','195-197 cm'],['5XL','115-130 kg','197-199 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ENFANTS (KIDS)</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['2 ans','12-17 kg','105-122 cm'],['4 ans','17-22 kg','122-135 cm'],['6 ans','22-27 kg','135-145 cm'],['8 ans','27-35 kg','145-155 cm'],['10 ans','35-45 kg','155-160 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ===== PERSONNALISATION ===== */}
          {activeTab === 'personalization' && (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{t('premium_personalization')}</CardTitle>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handlePersoExport} disabled={persoExporting}>
                    <Download size={14} /> {persoExporting ? t('premium_exporting') : t('premium_export_excel')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700"><strong>Client :</strong> {clientInfo?.firstname} {clientInfo?.name} — {clientInfo?.company_name}</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-medium w-10">#</TableHead>
                      <TableHead className="font-medium">Nom</TableHead>
                      <TableHead className="font-medium">Numéro</TableHead>
                      <TableHead className="font-medium">Taille</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {persoLines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs text-slate-500">{idx + 1}</TableCell>
                        <TableCell><Input value={line.nom} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, nom: e.target.value } : l))} placeholder="Nom" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.numero} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, numero: e.target.value } : l))} placeholder="N°" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.taille} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, taille: e.target.value } : l))} placeholder="Taille" className="h-8 text-sm" /></TableCell>
                        <TableCell>
                          {persoLines.length > 1 && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setPersoLines(prev => prev.filter((_, i) => i !== idx))}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => setPersoLines(prev => [...prev, { ...EMPTY_LINE }])}>
                  <Plus size={14} /> {t('premium_add_line')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ===== CONTACT ===== */}
          {activeTab === 'contact' && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">{t('premium_contact')}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {/* Contact person */}
                  <div className="flex items-center gap-4 bg-slate-50 p-5 rounded-xl">
                    <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg">
                      C
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{t('premium_contact_name')}</p>
                      <p className="text-sm text-slate-500">{t('premium_contact_role')}</p>
                    </div>
                  </div>

                  {/* Zalo + WhatsApp */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a href="https://zalo.me/0941524303" target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 p-4 text-white font-bold rounded-lg text-sm transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#0068FF' }}>
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2C6.48 2 2 6.07 2 11.09c0 2.87 1.36 5.43 3.48 7.14L4.58 22l3.89-1.97c1.14.37 2.35.57 3.53.57 5.52 0 10-4.07 10-9.09S17.52 2 12 2zm5.43 12.38c-.24.67-1.17 1.22-1.62 1.3-.45.09-.97.12-3.55-.74-3.3-1.1-5.42-4.5-5.58-4.71-.16-.21-1.3-1.74-1.3-3.31 0-1.57.82-2.35 1.12-2.67.29-.32.64-.4.86-.4s.43 0 .62.01c.2.01.47-.08.73.56.27.65.92 2.24 1 2.41.08.16.14.36.03.57-.11.21-.16.34-.32.52-.16.18-.34.41-.49.55-.16.16-.32.33-.14.65.18.32.82 1.36 1.77 2.2 1.21 1.08 2.24 1.42 2.56 1.58.32.16.5.13.69-.08.18-.21.79-.92 1-1.24.21-.32.43-.27.72-.16.29.11 1.87.88 2.19 1.04.32.16.53.24.61.37.08.13.08.76-.16 1.43z"/></svg>
                      {t('premium_zalo')}
                    </a>
                    <a href="https://wa.me/84941524303" target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 p-4 text-white font-bold rounded-lg text-sm transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#25D366' }}>
                      <MessageCircle size={20} />
                      {t('premium_whatsapp')} (+84 941 524 303)
                    </a>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg">
                    <Phone size={18} className="text-slate-500" />
                    <div>
                      <p className="text-xs text-slate-500">Telephone</p>
                      <a href="tel:+84941524303" className="text-sm font-medium text-blue-700 hover:underline">+84 941 524 303</a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </main>
      </div>

      {/* Level Detail Dialog */}
      <Dialog open={levelDialogOpen} onOpenChange={setLevelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown size={20} style={{ color: selectedLevel?.color }} />
              <span style={{ color: selectedLevel?.color }}>{selectedLevel?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedLevel && (
            <div className="space-y-4">
              <div className="text-center bg-slate-50 rounded-lg p-4">
                <p className="text-3xl font-black text-slate-900">{fmtVND(selectedLevel.threshold)}</p>
                <p className="text-sm text-slate-500">{language === 'vi' ? 'mỗi năm' : language === 'en' ? 'per year' : 'par an'}</p>
                <Badge className="mt-2 text-white text-sm px-3 py-1" style={{ backgroundColor: selectedLevel.color }}>
                  -{selectedLevel.discount}% {language === 'vi' ? 'giảm giá' : language === 'en' ? 'discount' : 'de réduction'}
                </Badge>
              </div>
              <div className="space-y-2">
                {getLevelBenefits(selectedLevel).map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-emerald-600 flex-shrink-0" />
                    <span className="font-medium text-slate-800">{b}</span>
                  </div>
                ))}
                {getLevelExcluded(selectedLevel).map((b, i) => (
                  <div key={`ex-${i}`} className="flex items-center gap-2 text-sm">
                    <X size={16} className="text-red-400 flex-shrink-0" />
                    <span className="text-slate-400">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
